import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { companyInfo } from '@/mocks/company';
import SEO from '@/components/feature/SEO';

const stats = [
  { value: '2000+', label: '研发人员', suffix: '' },
  { value: '500+', label: '核心专利', suffix: '' },
  { value: '¥150亿', label: '累计投资', suffix: '' },
  { value: '92%', label: 'DDR5芯片良率', suffix: '' },
];

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: '领航中国存储',
      subtitle: '尊龙集团 DDR5 正式量产',
      description: '基于1β先进制程，良率达业界领先水平，为中国半导体自主可控贡献力量',
      cta: '探索产品',
      link: '/category',
      bg: 'https://readdy.ai/api/search-image?query=Futuristic%20semiconductor%20cleanroom%20with%20silicon%20wafers%20illuminated%20by%20warm%20golden%20amber%20light%2C%20abstract%20circuit%20patterns%20floating%2C%20high%20tech%20minimalist%20corporate%20hero%20background%2C%20dark%20atmosphere%20with%20golden%20accents%2C%20cinematic%20wide%20angle%2C%20dramatic%20lighting&width=1920&height=900&seq=hero-slide-01&orientation=landscape',
    },
    {
      title: '创新存储 智联未来',
      subtitle: 'LPDDR5X 斩获CES创新奖',
      description: '业界领先能效比，为下一代AI终端提供澎湃动力，获国际权威认可',
      cta: '了解详情',
      link: '/articles/zunlong-lpddr5x-ces-award',
      bg: 'https://readdy.ai/api/search-image?query=Modern%20smartphone%20and%20AI%20chip%20concept%20with%20warm%20golden%20energy%20waves%20flowing%2C%20abstract%20tech%20background%2C%20dark%20atmosphere%20with%20amber%20and%20copper%20tones%2C%20cinematic%20futuristic%20aesthetic%2C%20dramatic%20lighting&width=1920&height=900&seq=hero-slide-02&orientation=landscape',
    },
    {
      title: '龙腾四海 芯耀中华',
      subtitle: '研发团队突破2000人',
      description: '全球五大研发中心协同创新，硕博占比超65%，构筑人才与技术护城河',
      cta: '加入我们',
      link: '/about',
      bg: 'https://readdy.ai/api/search-image?query=Futuristic%20research%20laboratory%20with%20scientists%20working%20on%20semiconductor%20technology%2C%20warm%20golden%20amber%20lighting%2C%20clean%20high%20tech%20environment%2C%20abstract%20data%20visualization%2C%20cinematic%20wide%20angle%2C%20dramatic%20corporate%20aesthetic&width=1920&height=900&seq=hero-slide-03&orientation=landscape',
    },
  ];

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <>
      <SEO
        title="首页"
        description="尊龙集团是一家一体化存储器制造公司，专注于动态随机存取存储芯片(DRAM)的设计、研发、生产和销售。总部位于安徽合肥，致力于成为中国领先的存储器解决方案提供商。"
        keywords="尊龙集团,DRAM芯片,存储器制造,半导体,合肥,集成电路,DDR5,LPDDR5X"
        canonicalPath="/"
      />

      <main>
        {/* Hero */}
        <section className="relative h-[420px] md:h-[600px] overflow-hidden">
          {slides.map((slide, i) => (
            <div
              key={i}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                i === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.bg}
                alt={slide.subtitle}
                className="w-full h-full object-cover object-top"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50" />
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6 max-w-[1400px] mx-auto">
              <div className="max-w-2xl">
                <p className="text-primary-300 text-sm md:text-base font-medium mb-2 md:mb-3 tracking-wider">
                  {slides[currentSlide].title}
                </p>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 md:mb-6 leading-tight">
                  {slides[currentSlide].subtitle}
                </h1>
                <p className="text-white/70 text-sm md:text-lg mb-6 md:mb-8 leading-relaxed max-w-xl">
                  {slides[currentSlide].description}
                </p>
                <Link
                  to={slides[currentSlide].link}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-all duration-200 text-sm md:text-base whitespace-nowrap"
                >
                  {slides[currentSlide].cta}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>

          <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`w-2 h-2 md:w-2.5 md:h-2.5 rounded-full transition-all duration-300 ${
                  i === currentSlide
                    ? 'bg-primary-500 w-6 md:w-8'
                    : 'bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`第${i + 1}张`}
              />
            ))}
          </div>
        </section>

        {/* Product Categories */}
        <section className="py-14 md:py-20 bg-background-50" data-product-shop>
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <p className="text-primary-600 text-sm font-medium tracking-wider mb-2">产品中心</p>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                全系列存储产品矩阵
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
                从芯片到模组，从消费级到企业级，尊龙集团提供覆盖全场景的存储器解决方案
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to="/category"
                  className="group bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 hover:border-primary-300 transition-all duration-200"
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-200">
                    <i className={`${cat.icon} text-lg md:text-xl`} />
                  </div>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2">
                    {cat.name}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-3">
                    {cat.description}
                  </p>
                  <div className="mt-3 flex items-center gap-1 text-primary-600 text-sm font-medium">
                    <span className="whitespace-nowrap">了解更多</span>
                    <i className="ri-arrow-right-line text-xs" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 md:py-16 bg-primary-500">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 mb-1">
                    {stat.value}
                    <span className="text-xl md:text-2xl">{stat.suffix}</span>
                  </div>
                  <p className="text-primary-200 text-xs md:text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Preview */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12">
              <div className="lg:w-1/2">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20corporate%20semiconductor%20headquarters%20building%20exterior%20with%20glass%20facade%2C%20warm%20golden%20sunset%20lighting%2C%20clean%20minimalist%20architecture%2C%20professional%20corporate%20photography%2C%20landscaped%20campus&width=700&height=500&seq=about-preview-01&orientation=landscape"
                  alt="尊龙集团总部"
                  className="w-full h-[280px] md:h-[380px] object-cover rounded-lg"
                />
              </div>
              <div className="lg:w-1/2">
                <p className="text-primary-600 text-sm font-medium tracking-wider mb-2">关于尊龙</p>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                  一体化存储器制造企业
                </h2>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                  {companyInfo.description}
                </p>
                <div className="flex flex-wrap gap-3">
                  {companyInfo.values.map((v) => (
                    <div key={v.title} className="flex items-center gap-2 bg-background-50 border border-background-200/70 rounded-full px-4 py-2">
                      <div className="w-5 h-5 flex items-center justify-center text-primary-500">
                        <i className={`${v.icon} text-sm`} />
                      </div>
                      <span className="text-xs md:text-sm font-medium text-foreground-800 whitespace-nowrap">{v.title}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 mt-6 text-primary-600 hover:text-primary-700 font-medium text-sm whitespace-nowrap"
                >
                  了解更多关于尊龙
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* News */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-10 md:mb-14">
              <div>
                <p className="text-primary-600 text-sm font-medium tracking-wider mb-2">新闻资讯</p>
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900">
                  最新动态
                </h2>
              </div>
              <Link
                to="/articles"
                className="hidden sm:inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium text-sm whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {articles.slice(0, 3).map((article) => (
                <Link
                  key={article.id}
                  to={`/articles/${article.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-200"
                >
                  <div className="h-44 md:h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                        {article.category}
                      </span>
                      <span className="text-xs text-foreground-400">{article.date}</span>
                    </div>
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.summary}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-6 text-center sm:hidden">
              <Link
                to="/articles"
                className="inline-flex items-center gap-1 text-primary-600 font-medium text-sm whitespace-nowrap"
              >
                查看全部新闻
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="relative py-14 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20warm%20golden%20amber%20geometric%20circuit%20pattern%20on%20dark%20background%2C%20elegant%20tech%20texture%2C%20minimalist%20corporate%20banner%20design%2C%20subtle%20light%20rays%2C%20professional%20background&width=1920&height=600&seq=cta-contact-01&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60" />
          <div className="relative max-w-[1400px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
              携手共创存储未来
            </h2>
            <p className="text-white/70 text-sm md:text-base mb-8 max-w-xl mx-auto">
              无论您是寻求高性能存储方案的客户，还是希望加入尊龙的人才，我们都期待与您沟通
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-all duration-200 text-sm whitespace-nowrap"
              >
                <i className="ri-mail-line" />
                联系我们
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-full transition-all duration-200 text-sm whitespace-nowrap backdrop-blur-sm"
              >
                <i className="ri-stack-line" />
                浏览产品
              </Link>
            </div>
          </div>
        </section>
      </main>

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '尊龙集团首页',
          description: '尊龙集团是一家一体化存储器制造公司，专注于动态随机存取存储芯片(DRAM)的设计、研发、生产和销售。',
          url: 'https://www.zunlonggroup.com/',
          isPartOf: {
            '@type': 'WebSite',
            name: '尊龙集团中国官方网站',
            url: 'https://www.zunlonggroup.com/',
          },
        })}
      </script>
    </>
  );
}